import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { apiClient } from '../api/client';
import {
  PRIMARY,
  TEXT_DARK,
  TEXT_MUTED,
  TEXT_SECONDARY,
  BG_CARD,
  BG_SURFACE,
  BORDER_LIGHT,
  FONT_HEADING,
} from '../components/legacy-ball/table-management/designTokens';

// ── Types ───────────────────────────────────────────────────────────────────

interface StoryData {
  id: string;
  title: string;
  narrative: string;
  culturalInsight: string;
  krioProverbId: string | null;
  heritageFact: string | null;
  status: 'Generated' | 'Approved' | 'Declined' | 'Flagged' | 'Removed';
  guestName: string;
  chapterAffiliation: string | null;
  familyName: string | null;
  reunionYear: number;
  featured: boolean;
  approvedAt: string | null;
  translation: {
    id: string;
    krioText: string;
    confidenceScore: number;
    confidenceLevel: 'High' | 'Moderate' | 'Low';
    status: 'Available' | 'PendingReview' | 'Approved';
  } | null;
}

interface TranslationData {
  english: string;
  krio: string;
  sideBySide: { english: string; krio: string }[];
  confidenceScore: number;
  confidenceLevel: string;
  status: string;
}

interface EducationalAnnotation {
  word: string;
  pronunciation: string;
  meaning: string;
  culturalNotes: string;
}

interface EducationalData {
  storyId: string;
  annotations: EducationalAnnotation[];
  message?: string;
}

type TranslationView = 'english' | 'krio' | 'bilingual';

// ── Component ───────────────────────────────────────────────────────────────

export function StoryBuilderMyStoryPage() {
  const navigate = useNavigate();

  const [story, setStory] = useState<StoryData | null>(null);
  const [translation, setTranslation] = useState<TranslationData | null>(null);
  const [educational, setEducational] = useState<EducationalData | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const [translationView, setTranslationView] = useState<TranslationView>('english');
  const [educationalMode, setEducationalMode] = useState(false);
  const [activeAnnotation, setActiveAnnotation] = useState<EducationalAnnotation | null>(null);

  // ── Fetch story ─────────────────────────────────────────────────────────
  const fetchStory = useCallback(async () => {
    try {
      setLoading(true);
      setError(null);
      const data = await apiClient<StoryData>('/story-builder/stories/mine');
      setStory(data);

      // If story is Generated, redirect to review page
      if (data.status === 'Generated') {
        navigate('/story-builder/review', { replace: true });
        return;
      }

      // If approved and has translation available, fetch translation
      if (data.status === 'Approved' && data.translation) {
        try {
          const transData = await apiClient<TranslationData>(
            `/story-builder/stories/${data.id}/translation`,
          );
          setTranslation(transData);
        } catch {
          // Translation fetch failed silently — show English only
        }
      }
    } catch (err: any) {
      if (err?.statusCode === 404) {
        setStory(null);
      } else {
        setError(err?.message || 'Failed to load your story.');
      }
    } finally {
      setLoading(false);
    }
  }, [navigate]);

  useEffect(() => {
    fetchStory();
  }, [fetchStory]);

  // ── Fetch educational annotations ───────────────────────────────────────
  const fetchEducational = useCallback(async () => {
    if (!story) return;
    try {
      const data = await apiClient<EducationalData>(
        `/story-builder/stories/${story.id}/educational`,
      );
      setEducational(data);
    } catch {
      setEducational({ storyId: story.id, annotations: [], message: 'Unable to load annotations.' });
    }
  }, [story]);

  useEffect(() => {
    if (educationalMode && story && !educational) {
      fetchEducational();
    }
  }, [educationalMode, story, educational, fetchEducational]);

  // Auto-poll for translation completion (every 5s while translation is missing)
  const noTranslationYet = story?.status === 'Approved' && !story?.translation;
  useEffect(() => {
    if (!noTranslationYet || !story) return;
    const interval = setInterval(() => {
      fetchStory();
    }, 5000);
    return () => clearInterval(interval);
  }, [noTranslationYet, story, fetchStory]);

  // ── Loading state ───────────────────────────────────────────────────────
  if (loading) {
    return (
      <div style={styles.container}>
        <div style={styles.loadingCard}>
          <p style={{ color: TEXT_MUTED, fontSize: '1rem' }}>Loading your story...</p>
        </div>
      </div>
    );
  }

  // ── Error state ─────────────────────────────────────────────────────────
  if (error) {
    return (
      <div style={styles.container}>
        <div style={styles.card}>
          <p style={{ color: '#d32f2f', marginBottom: '1rem' }}>{error}</p>
          <button style={styles.btnPrimary} onClick={fetchStory}>
            Retry
          </button>
        </div>
      </div>
    );
  }

  // ── No story yet ────────────────────────────────────────────────────────
  if (!story) {
    return (
      <div style={styles.container}>
        <div style={styles.emptyCard}>
          <div style={{ fontSize: '3rem', marginBottom: '1rem' }}>📖</div>
          <h2 style={{ ...styles.heading, marginBottom: '0.5rem' }}>Your Story Awaits</h2>
          <p style={{ color: TEXT_SECONDARY, marginBottom: '1.5rem', maxWidth: 400, textAlign: 'center' as const }}>
            You haven't started your story yet. Answer a few questions about your heritage and
            connection to the KDU community, and we'll create a personalized narrative just for you.
          </p>
          <button
            style={styles.btnPrimary}
            onClick={() => navigate('/story-builder/interview')}
          >
            Start Your Story
          </button>
        </div>
      </div>
    );
  }

  // ── Story declined ──────────────────────────────────────────────────────
  if (story.status === 'Declined') {
    return (
      <div style={styles.container}>
        <div style={styles.emptyCard}>
          <div style={{ fontSize: '3rem', marginBottom: '1rem' }}>📝</div>
          <h2 style={{ ...styles.heading, marginBottom: '0.5rem' }}>Story Declined</h2>
          <p style={{ color: TEXT_SECONDARY, marginBottom: '1.5rem', maxWidth: 400, textAlign: 'center' as const }}>
            You previously declined publication of your story. You can start fresh with a new interview
            to create a new story.
          </p>
          <button
            style={styles.btnPrimary}
            onClick={() => navigate('/story-builder/interview')}
          >
            Start Again
          </button>
        </div>
      </div>
    );
  }

  // ── Story flagged/removed ───────────────────────────────────────────────
  if (story.status === 'Flagged' || story.status === 'Removed') {
    return (
      <div style={styles.container}>
        <div style={styles.card}>
          <h2 style={styles.heading}>Story Unavailable</h2>
          <p style={{ color: TEXT_SECONDARY, marginTop: '0.5rem' }}>
            Your story is currently under review by the administrative team.
            Please check back later.
          </p>
        </div>
      </div>
    );
  }

  // ── Determine translation availability ──────────────────────────────────
  const hasTranslation = !!translation;
  const translationPending =
    story.translation && story.translation.status === 'PendingReview';
  const noTranslation = !story.translation;

  // Educational mode can only be active if translation exists
  const canUseEducational = hasTranslation;
  const educationalDisabledMessage = !canUseEducational
    ? 'A Krio translation is required for Educational Mode.'
    : null;

  // ── Render highlighted Krio text ────────────────────────────────────────
  function renderKrioText(text: string) {
    if (!educationalMode || !educational || educational.annotations.length === 0) {
      return <p style={styles.narrative}>{text}</p>;
    }

    const highlightedWords = new Set(
      educational.annotations.map((a) => a.word.toLowerCase()),
    );

    const words = text.split(/(\s+)/);
    return (
      <p style={styles.narrative}>
        {words.map((word, idx) => {
          const cleanWord = word.replace(/[^a-zA-ZÀ-ÿ'-]/g, '').toLowerCase();
          if (highlightedWords.has(cleanWord)) {
            return (
              <span
                key={idx}
                style={styles.highlightedWord}
                role="button"
                tabIndex={0}
                aria-label={`Learn about the word: ${cleanWord}`}
                onClick={() => {
                  const annotation = educational!.annotations.find(
                    (a) => a.word.toLowerCase() === cleanWord,
                  );
                  if (annotation) setActiveAnnotation(annotation);
                }}
                onKeyDown={(e) => {
                  if (e.key === 'Enter' || e.key === ' ') {
                    e.preventDefault();
                    const annotation = educational!.annotations.find(
                      (a) => a.word.toLowerCase() === cleanWord,
                    );
                    if (annotation) setActiveAnnotation(annotation);
                  }
                }}
              >
                {word}
              </span>
            );
          }
          return <span key={idx}>{word}</span>;
        })}
      </p>
    );
  }

  // ── Render story content based on translation view ──────────────────────
  function renderStoryContent() {
    if (translationView === 'english' || !hasTranslation) {
      return (
        <div>
          <p style={styles.narrative}>{story!.narrative}</p>
        </div>
      );
    }

    if (translationView === 'krio') {
      return <div>{renderKrioText(translation!.krio)}</div>;
    }

    // Bilingual side-by-side
    return (
      <div style={styles.bilingualContainer}>
        {translation!.sideBySide.map((pair, idx) => (
          <div key={idx} style={styles.bilingualRow}>
            <div style={styles.bilingualCol}>
              <span style={styles.langLabel}>English</span>
              <p style={styles.bilingualText}>{pair.english}</p>
            </div>
            <div style={styles.bilingualCol}>
              <span style={styles.langLabel}>Krio</span>
              {educationalMode && educational && educational.annotations.length > 0 ? (
                renderKrioText(pair.krio)
              ) : (
                <p style={styles.bilingualText}>{pair.krio}</p>
              )}
            </div>
          </div>
        ))}
      </div>
    );
  }

  // ── Main approved story view ────────────────────────────────────────────
  return (
    <div style={styles.container}>
      <style>{`@keyframes spin { to { transform: rotate(360deg); } }`}</style>
      {/* Header */}
      <div style={styles.header}>
        <h1 style={styles.pageTitle}>📖 My Story</h1>
        {story.featured && (
          <span style={styles.featuredBadge}>⭐ Featured</span>
        )}
      </div>

      {/* Story Card */}
      <div style={styles.card}>
        {/* Story title */}
        <h2 style={styles.storyTitle}>{story.title}</h2>
        <p style={styles.meta}>
          {story.guestName}
          {story.chapterAffiliation && ` • ${story.chapterAffiliation}`}
          {story.familyName && ` • ${story.familyName} Family`}
        </p>

        {/* Translation view toggle */}
        {hasTranslation && (
          <nav style={styles.viewToggleNav} aria-label="Translation view">
            {(['english', 'krio', 'bilingual'] as TranslationView[]).map((view) => (
              <button
                key={view}
                style={
                  translationView === view
                    ? styles.viewToggleActive
                    : styles.viewToggleBtn
                }
                onClick={() => setTranslationView(view)}
                aria-current={translationView === view ? 'page' : undefined}
              >
                {view === 'english' ? 'English' : view === 'krio' ? 'Krio' : 'Side-by-Side'}
              </button>
            ))}
          </nav>
        )}

        {/* No translation available message */}
        {noTranslation && (
          <div style={styles.infoMessage}>
            <span style={styles.translationSpinner} />
            Translating to Krio — this usually takes about 30 seconds...
          </div>
        )}

        {/* Translation pending review message */}
        {translationPending && !hasTranslation && (
          <div style={styles.infoMessage}>
            <span style={{ marginRight: '0.5rem' }}>⏳</span>
            Your Krio translation is being reviewed and will be available soon.
          </div>
        )}

        {/* Retry Translation button — shown when translation is missing or failed */}
        {!hasTranslation && story.status === 'Approved' && (
          <div style={{ marginTop: '1rem', textAlign: 'center' as const }}>
            <button
              style={styles.btnPrimary}
              onClick={async () => {
                try {
                  await apiClient(`/story-builder/stories/${story.id}/retranslate`, { method: 'POST' });
                  fetchStory();
                } catch (err: any) {
                  alert(err?.message || 'Translation failed. Please try again.');
                }
              }}
            >
              🔄 Retry Krio Translation
            </button>
          </div>
        )}

        {/* Story narrative */}
        <div style={{ marginTop: '1.5rem' }}>
          {renderStoryContent()}
        </div>

        {/* Cultural Insight */}
        <div style={styles.insightSection}>
          <h3 style={styles.sectionLabel}>Cultural Insight</h3>
          <p style={styles.insightText}>{story.culturalInsight}</p>
        </div>

        {/* Heritage Fact */}
        {story.heritageFact && (
          <div style={styles.insightSection}>
            <h3 style={styles.sectionLabel}>Heritage Fact</h3>
            <p style={styles.insightText}>{story.heritageFact}</p>
          </div>
        )}

        {/* Krio Proverb (if present in story data — shown from translation response) */}
        {story.krioProverbId && (
          <div style={styles.proverbSection}>
            <h3 style={styles.sectionLabel}>🌿 Krio Proverb</h3>
            <p style={styles.proverbText}>
              <em>A proverb has been attached to celebrate your story's themes.</em>
            </p>
          </div>
        )}
      </div>

      {/* Educational Mode Toggle */}
      <div style={styles.educationalCard}>
        <div style={styles.educationalHeader}>
          <div>
            <h3 style={styles.educationalTitle}>🎓 Learn Krio From My Story</h3>
            <p style={styles.educationalDesc}>
              {canUseEducational
                ? 'Tap highlighted words to learn their pronunciation, meaning, and cultural context.'
                : educationalDisabledMessage}
            </p>
          </div>
          <label style={styles.toggleLabel}>
            <input
              type="checkbox"
              checked={educationalMode}
              disabled={!canUseEducational}
              onChange={(e) => {
                setEducationalMode(e.target.checked);
                if (!e.target.checked) setActiveAnnotation(null);
              }}
              style={styles.toggleInput}
              aria-label="Toggle Educational Mode"
            />
            <span
              style={{
                ...styles.toggleTrack,
                backgroundColor: educationalMode ? PRIMARY : '#ccc',
                opacity: canUseEducational ? 1 : 0.5,
              }}
            >
              <span
                style={{
                  ...styles.toggleThumb,
                  transform: educationalMode ? 'translateX(20px)' : 'translateX(2px)',
                }}
              />
            </span>
          </label>
        </div>

        {/* Educational message when no annotations */}
        {educationalMode && educational?.message && (
          <p style={{ color: TEXT_MUTED, fontSize: '0.875rem', marginTop: '0.75rem' }}>
            {educational.message}
          </p>
        )}
      </div>

      {/* Annotation Popup */}
      {activeAnnotation && (
        <div style={styles.annotationOverlay} onClick={() => setActiveAnnotation(null)}>
          <div
            style={styles.annotationPopup}
            onClick={(e) => e.stopPropagation()}
            role="dialog"
            aria-label={`Annotation for ${activeAnnotation.word}`}
          >
            <button
              style={styles.annotationClose}
              onClick={() => setActiveAnnotation(null)}
              aria-label="Close annotation"
            >
              ✕
            </button>
            <h4 style={styles.annotationWord}>{activeAnnotation.word}</h4>
            <div style={styles.annotationField}>
              <span style={styles.annotationLabel}>Pronunciation</span>
              <span style={styles.annotationValue}>{activeAnnotation.pronunciation}</span>
            </div>
            <div style={styles.annotationField}>
              <span style={styles.annotationLabel}>Meaning</span>
              <span style={styles.annotationValue}>{activeAnnotation.meaning}</span>
            </div>
            {activeAnnotation.culturalNotes && (
              <div style={styles.annotationField}>
                <span style={styles.annotationLabel}>Cultural Notes</span>
                <span style={styles.annotationValue}>{activeAnnotation.culturalNotes}</span>
              </div>
            )}
          </div>
        </div>
      )}
    </div>
  );
}

// ── Styles ──────────────────────────────────────────────────────────────────

const styles: Record<string, React.CSSProperties> = {
  container: {
    padding: '1.5rem',
    maxWidth: 800,
    margin: '0 auto',
    minHeight: '60vh',
  },
  loadingCard: {
    display: 'flex',
    justifyContent: 'center',
    alignItems: 'center',
    minHeight: 200,
  },
  header: {
    display: 'flex',
    alignItems: 'center',
    gap: '0.75rem',
    marginBottom: '1.5rem',
  },
  pageTitle: {
    fontFamily: FONT_HEADING,
    fontSize: '1.5rem',
    fontWeight: 700,
    color: TEXT_DARK,
    margin: 0,
  },
  featuredBadge: {
    backgroundColor: '#fef3c7',
    color: '#92400e',
    padding: '2px 10px',
    borderRadius: 12,
    fontSize: '0.75rem',
    fontWeight: 600,
  },
  card: {
    backgroundColor: BG_CARD,
    borderRadius: 12,
    border: `1px solid ${BORDER_LIGHT}`,
    padding: '2rem',
    marginBottom: '1rem',
  },
  emptyCard: {
    backgroundColor: BG_CARD,
    borderRadius: 12,
    border: `1px solid ${BORDER_LIGHT}`,
    padding: '3rem 2rem',
    display: 'flex',
    flexDirection: 'column' as const,
    alignItems: 'center',
    textAlign: 'center' as const,
  },
  heading: {
    fontFamily: FONT_HEADING,
    fontSize: '1.25rem',
    fontWeight: 700,
    color: TEXT_DARK,
    margin: 0,
  },
  storyTitle: {
    fontFamily: FONT_HEADING,
    fontSize: '1.4rem',
    fontWeight: 700,
    color: TEXT_DARK,
    margin: '0 0 0.5rem',
  },
  meta: {
    fontSize: '0.875rem',
    color: TEXT_MUTED,
    margin: '0 0 1rem',
  },
  narrative: {
    fontSize: '1rem',
    lineHeight: 1.7,
    color: TEXT_DARK,
    margin: 0,
  },
  insightSection: {
    marginTop: '1.5rem',
    paddingTop: '1.25rem',
    borderTop: `1px solid ${BORDER_LIGHT}`,
  },
  sectionLabel: {
    fontSize: '0.8rem',
    fontWeight: 600,
    textTransform: 'uppercase' as const,
    letterSpacing: '0.05em',
    color: TEXT_MUTED,
    margin: '0 0 0.5rem',
  },
  insightText: {
    fontSize: '0.95rem',
    lineHeight: 1.6,
    color: TEXT_SECONDARY,
    margin: 0,
    fontStyle: 'italic' as const,
  },
  proverbSection: {
    marginTop: '1.5rem',
    paddingTop: '1.25rem',
    borderTop: `1px solid ${BORDER_LIGHT}`,
    backgroundColor: BG_SURFACE,
    margin: '1.5rem -2rem -2rem',
    padding: '1.25rem 2rem 2rem',
    borderRadius: '0 0 12px 12px',
  },
  proverbText: {
    fontSize: '0.95rem',
    lineHeight: 1.6,
    color: TEXT_SECONDARY,
    margin: 0,
  },
  // View toggle
  viewToggleNav: {
    display: 'flex',
    gap: '0.25rem',
    backgroundColor: BG_SURFACE,
    borderRadius: 8,
    padding: 3,
    marginTop: '0.5rem',
  },
  viewToggleBtn: {
    flex: 1,
    padding: '0.5rem 1rem',
    border: 'none',
    borderRadius: 6,
    cursor: 'pointer',
    fontSize: '0.85rem',
    fontWeight: 500,
    color: TEXT_MUTED,
    backgroundColor: 'transparent',
    transition: 'all 0.15s',
  },
  viewToggleActive: {
    flex: 1,
    padding: '0.5rem 1rem',
    border: 'none',
    borderRadius: 6,
    cursor: 'pointer',
    fontSize: '0.85rem',
    fontWeight: 600,
    color: PRIMARY,
    backgroundColor: BG_CARD,
    boxShadow: '0 1px 3px rgba(0,0,0,0.08)',
  },
  // Info messages
  infoMessage: {
    display: 'flex',
    alignItems: 'center',
    backgroundColor: '#eff6ff',
    border: '1px solid #bfdbfe',
    borderRadius: 8,
    padding: '0.75rem 1rem',
    fontSize: '0.875rem',
    color: '#1e40af',
    marginTop: '1rem',
  },
  translationSpinner: {
    display: 'inline-block',
    width: 16,
    height: 16,
    border: '2px solid #bfdbfe',
    borderTopColor: '#1e40af',
    borderRadius: '50%',
    animation: 'spin 0.8s linear infinite',
    marginRight: '0.6rem',
    flexShrink: 0,
  },
  // Bilingual
  bilingualContainer: {
    display: 'flex',
    flexDirection: 'column' as const,
    gap: '1rem',
  },
  bilingualRow: {
    display: 'grid',
    gridTemplateColumns: '1fr 1fr',
    gap: '1.5rem',
    paddingBottom: '1rem',
    borderBottom: `1px solid ${BORDER_LIGHT}`,
  },
  bilingualCol: {
    display: 'flex',
    flexDirection: 'column' as const,
    gap: '0.25rem',
  },
  langLabel: {
    fontSize: '0.7rem',
    fontWeight: 600,
    textTransform: 'uppercase' as const,
    letterSpacing: '0.05em',
    color: TEXT_MUTED,
  },
  bilingualText: {
    fontSize: '0.95rem',
    lineHeight: 1.6,
    color: TEXT_DARK,
    margin: 0,
  },
  // Educational mode
  educationalCard: {
    backgroundColor: BG_CARD,
    borderRadius: 12,
    border: `1px solid ${BORDER_LIGHT}`,
    padding: '1.25rem 1.5rem',
    marginBottom: '1rem',
  },
  educationalHeader: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'flex-start',
    gap: '1rem',
  },
  educationalTitle: {
    fontSize: '1rem',
    fontWeight: 600,
    color: TEXT_DARK,
    margin: '0 0 0.25rem',
  },
  educationalDesc: {
    fontSize: '0.85rem',
    color: TEXT_MUTED,
    margin: 0,
  },
  // Toggle switch
  toggleLabel: {
    position: 'relative' as const,
    display: 'inline-flex',
    alignItems: 'center',
    cursor: 'pointer',
    flexShrink: 0,
  },
  toggleInput: {
    position: 'absolute' as const,
    opacity: 0,
    width: 0,
    height: 0,
  },
  toggleTrack: {
    display: 'inline-block',
    width: 44,
    height: 24,
    borderRadius: 12,
    transition: 'background-color 0.2s',
    position: 'relative' as const,
  },
  toggleThumb: {
    display: 'block',
    width: 20,
    height: 20,
    borderRadius: '50%',
    backgroundColor: '#fff',
    boxShadow: '0 1px 3px rgba(0,0,0,0.2)',
    transition: 'transform 0.2s',
    position: 'absolute' as const,
    top: 2,
  },
  // Highlighted word
  highlightedWord: {
    backgroundColor: '#dcfce7',
    borderBottom: `2px solid ${PRIMARY}`,
    padding: '1px 2px',
    borderRadius: 3,
    cursor: 'pointer',
    transition: 'background-color 0.15s',
  },
  // Annotation popup
  annotationOverlay: {
    position: 'fixed' as const,
    top: 0,
    left: 0,
    right: 0,
    bottom: 0,
    backgroundColor: 'rgba(0,0,0,0.4)',
    display: 'flex',
    justifyContent: 'center',
    alignItems: 'center',
    zIndex: 1000,
    padding: '1rem',
  },
  annotationPopup: {
    backgroundColor: BG_CARD,
    borderRadius: 12,
    padding: '1.5rem',
    maxWidth: 360,
    width: '100%',
    position: 'relative' as const,
    boxShadow: '0 20px 60px rgba(0,0,0,0.2)',
  },
  annotationClose: {
    position: 'absolute' as const,
    top: 12,
    right: 12,
    background: 'none',
    border: 'none',
    fontSize: '1.1rem',
    cursor: 'pointer',
    color: TEXT_MUTED,
    padding: '4px 6px',
    borderRadius: 4,
    lineHeight: 1,
  },
  annotationWord: {
    fontFamily: FONT_HEADING,
    fontSize: '1.25rem',
    fontWeight: 700,
    color: PRIMARY,
    margin: '0 0 1rem',
    textTransform: 'capitalize' as const,
  },
  annotationField: {
    display: 'flex',
    flexDirection: 'column' as const,
    gap: '0.2rem',
    marginBottom: '0.75rem',
  },
  annotationLabel: {
    fontSize: '0.7rem',
    fontWeight: 600,
    textTransform: 'uppercase' as const,
    letterSpacing: '0.05em',
    color: TEXT_MUTED,
  },
  annotationValue: {
    fontSize: '0.95rem',
    color: TEXT_DARK,
    lineHeight: 1.5,
  },
  // Buttons
  btnPrimary: {
    backgroundColor: PRIMARY,
    color: '#fff',
    border: 'none',
    borderRadius: 8,
    padding: '0.75rem 1.5rem',
    fontSize: '0.95rem',
    fontWeight: 600,
    cursor: 'pointer',
    transition: 'opacity 0.15s',
  },
};
